import OnboardingModal from '@components/Onboarding/OnboardingModal.vue';

import { defineStore, acceptHMRUpdate } from 'pinia';
import { computed, ref } from 'vue';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { createClient, OperationResult, ClientOptions, fetchExchange } from '@urql/core';
import { ME_QUERY } from '@graphql/queries/auth';
import { Person, MeResponse } from '@graphql/types/auth';
import { PersonRoleTypeEnum, RoleStatusEnum } from '@graphql/enums/auth';

import type Echo from 'laravel-echo';

const clientOptions: ClientOptions = {
  url: import.meta.env.VITE_PROXY_URL ?? '',
  fetchOptions: {
    headers: {
      environment: import.meta.env.VITE_GRAPHQL_ENVIRONMENT,
    },
  },
  exchanges: [fetchExchange],
};

const gqlClient = createClient(clientOptions);

const storeSetup = () => {
  const user = ref<Person | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const expiration = ref<number | null>(null);
  const isLoading = ref<boolean>(false);
  const isOnfidoPreliminaryChecked = ref<boolean>(false);
  const authenticated = computed(() => !isLoading.value && user.value !== null);

  const shouldRefreshToken = computed((): boolean => {
    if (!refreshToken.value) return false;

    if (typeof accessToken.value === 'string') {
      const jwt = jwtDecode<JwtPayload>(accessToken.value);

      if (jwt?.exp && jwt?.exp < Date.now()) return false;
    }

    return true;
  });

  const isActiveAdvisor = computed(() => {
    return !!(user?.value?.roles.find(role => role.type === PersonRoleTypeEnum.ADVISOR && role.status === RoleStatusEnum.ACTIVE));
  });

  const setOnboardingModal = () => {
    useModal({
      component: OnboardingModal,
      isCloseable: false,
    });
  };

  const updateTokens = (
    _accessToken: string | null,
    _refreshToken: string | null,
    _expiration: number | null,
  ) => {
    accessToken.value = _accessToken;
    refreshToken.value = _refreshToken;
    expiration.value = _expiration;

    const { $echo } = useNuxtApp();
    ($echo as Echo).options.auth.headers.Authorization = _accessToken ? `Bearer ${_accessToken}` : undefined;
  };

  const login = (
    _user: Person,
    _accessToken: string,
    _refreshToken: string,
    _expiration: number,
  ) => {
    user.value = _user;
    updateTokens(_accessToken, _refreshToken, _expiration);
  };

  const logout = () => {
    user.value = null;
    updateTokens(null, null, null);
  };

  const getUserPhoneNumbers = () => {
    if (user.value?.roles[0].role_data.phones && user.value.roles[0].role_data.phones.length > 0) {
      return user.value?.roles[0].role_data.phones[0]?.phone;
    }
    return '';
  };

  const refreshUser = () => {
    return accessToken.value ? gqlClient
      .query<MeResponse>(ME_QUERY, undefined, {
        requestPolicy: 'network-only',
        fetchOptions: {
          cache: 'no-cache',
          headers: {
            authorization: `Bearer ${accessToken.value}`,
            environment: import.meta.env.VITE_GRAPHQL_ENVIRONMENT,
          },
        },
      })
      .toPromise()
      .then((result: OperationResult<MeResponse>) => {
        if (result.data?.me) {
          user.value = result.data.me;
        } else {
          updateTokens(null, null, null);
        }
      })
      .catch(() => {
        updateTokens(null, null, null);
      }) : null;
  };


  return {
    // State
    user,
    accessToken,
    refreshToken,
    expiration,
    isLoading,
    isOnfidoPreliminaryChecked,

    // Getters
    authenticated,
    shouldRefreshToken,
    isActiveAdvisor,

    // Actions
    login,
    logout,
    updateTokens,
    refreshUser,
    getUserPhoneNumbers,
    setOnboardingModal,
  };
};

export const useAuthStore = defineStore('auth', storeSetup, {
  persistedState: {
    merge: (state, savedState) => {
      if (savedState.accessToken) {
        state.isLoading = true;

        gqlClient
          .query<MeResponse>(ME_QUERY, undefined, {
            requestPolicy: 'network-only',
            fetchOptions: {
              cache: 'no-cache',
              headers: {
                authorization: `Bearer ${savedState.accessToken}`,
                environment: import.meta.env.VITE_GRAPHQL_ENVIRONMENT,
              },
            },
          })
          .toPromise()
          .then((result: OperationResult<MeResponse>) => {
            if (result.data?.me) {
              state.user = result.data.me;
            } else {
              state.accessToken = null;
              state.refreshToken = null;
              state.expiration = null;
            }
          })
          .catch(() => {
            state.accessToken = null;
            state.refreshToken = null;
          })
          .finally(() => {
            state.isLoading = false;
          });
      }
      state.accessToken = savedState.accessToken ?? null;
      state.refreshToken = savedState.refreshToken ?? null;
      state.expiration = savedState.expiration ?? null;

      return state;
    },
    excludePaths: ['user'],
  },
  share: {
    initialize: true,
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
